import { Activity, Plus, Tag, Bell, UserPlus, Heart, MessageSquare, ChevronRight, ChevronLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"

export function RightSidebar() {
  const [isVisible, setIsVisible] = useState(false);
  
  // Load visibility state from localStorage
  useEffect(() => {
    const storedState = localStorage.getItem('rightSidebar_visible');
    if (storedState !== null) {
      setIsVisible(storedState === 'true');
    }
  }, []);
  
  // Save visibility state to localStorage
  useEffect(() => {
    localStorage.setItem('rightSidebar_visible', String(isVisible));
  }, [isVisible]);

  if (!isVisible) {
    return (
      <div className="fixed right-0 top-[6rem]">
        <button
          onClick={() => setIsVisible(true)}
          className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground relative -left-3 top-6 z-50 h-6 w-6 rounded-full border bg-background dark:bg-card shadow-md"
          aria-label="Show sidebar"
        >
          <ChevronLeft className="h-4 w-6" />
        </button>
      </div>
    );
  }

  return (
    <div className="sticky top-[6rem] h-[calc(100vh-4rem)] w-96 flex-shrink-0 flex flex-col gap-4 py-6 pr-6 overflow-y-auto relative">
      {/* Hide button */}
      <button
        onClick={() => setIsVisible(false)}
        className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground absolute -left-3 top-6 z-50 h-6 w-6 rounded-full border bg-background dark:bg-card shadow-md"
        aria-label="Hide sidebar"
      >
        <ChevronRight className="h-4 w-6" />
      </button>
      
      {/* My Activity Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex-shrink-0 backdrop-blur supports-[backdrop-filter]:bg-gray-50/95 dark:supports-[backdrop-filter]:bg-gray-800/50">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Activity className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">My Activity</h2>
            </div>
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700">
              View all
            </Button>
          </div>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Plus className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">Added new link</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">2 minutes ago</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Tag className="w-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">Updated tags</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">1 hour ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Notifications Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex-shrink-0 backdrop-blur supports-[backdrop-filter]:bg-gray-50/95 dark:supports-[backdrop-filter]:bg-gray-800/50">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Bell className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">Community</h2>
            </div>
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700">
              See all
            </Button>
          </div>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500"></div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-blue-500 border-2 border-white dark:border-gray-800 rounded-full flex items-center justify-center">
                  <UserPlus className="w-2 h-2 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">Sarah Chen started following you</p>
                <p className="text-xs text-blue-500 dark:text-blue-400">2 minutes ago</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500"></div>
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-red-500 border-2 border-white dark:border-gray-800 rounded-full flex items-center justify-center">
                  <Heart className="w-2 h-2 text-white fill-current" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">Alex Morgan liked your collection</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">15 minutes ago</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm min-h-[600px] max-h-[800px] backdrop-blur supports-[backdrop-filter]:bg-gray-50/95 dark:supports-[backdrop-filter]:bg-gray-800/50">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <MessageSquare className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="font-semibold text-gray-900 dark:text-gray-100">Copilot</h2>
            </div>
            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700">
              New chat
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex-shrink-0 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="bg-gray-100 dark:bg-gray-900 rounded-2xl rounded-tl-none p-3">
                  <p className="text-sm text-gray-900 dark:text-gray-100">I noticed you've added several links about machine learning recently. Would you like me to help organize them into a new collection?</p>
                </div>
                <p className="text-xs text-gray-500 mt-2">2m ago</p>
              </div>
            </div>
            <div className="flex items-start gap-3 justify-end">
              <div className="flex-1 min-w-0">
                <div className="bg-blue-500 rounded-2xl rounded-tr-none p-3">
                  <p className="text-sm text-white">Yes, that would be helpful! What did you have in mind?</p>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-right">1m ago</p>
              </div>
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex-shrink-0"></div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex-shrink-0 flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="bg-gray-100 dark:bg-gray-900 rounded-2xl rounded-tl-none p-3">
                  <p className="text-sm text-gray-900 dark:text-gray-100">I can create a "ML Resources" collection and categorize them by topic (e.g., Deep Learning, NLP, Computer Vision). I'll also suggest some relevant tags based on the content. Would you like me to proceed?</p>
                </div>
                <p className="text-xs text-gray-500 mt-2">Just now</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
} 